import type { Lesson } from './courses';

/**
 * Parcours : Stripe Billing V2 — 5 lecons.
 */
export const STRIPE_BILLING_V2_LESSONS: Lesson[] = [
  {
    slug: 'five-plans',
    title: 'Les 5 plans tarifaires (Free / Solo / Starter / Team / Enterprise)',
    duration: '4 min',
    glossaryTerms: ['plan', 'mrr', 'pricing'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Feedback Orchestrator propose 5 plans tarifaires conçus pour accompagner chaque etape de croissance :\n\n- **Free** — Decouverte sans engagement, BYOK (Bring Your Own Key) pour l'IA, fonctionnalites essentielles\n- **Solo** — Pour les independants et freelances, 1 siege, acces complet aux analyses\n- **Starter** — Pour les petites equipes (jusqu'a 5 sieges), collaboration de base\n- **Team** — Pour les equipes structurees (jusqu'a 20 sieges), features avancees, priorite support\n- **Enterprise** — Sur mesure, sieges illimites, SLA garanti, account manager dedie\n\nChaque plan est defini comme un Stripe Product avec des Price associes dans 3 devises (EUR, USD, XOF).",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Une grille tarifaire claire et progressive est la cle d'un SaaS rentable. Elle permet de :\n\n- **Reduire le churn** en offrant un upgrade naturel quand les besoins evoluent\n- **Maximiser le LTV** en capturant la valeur a chaque segment\n- **Simplifier le go-to-market** avec un positionnement clair par persona\n\nLe plan Free avec BYOK est une decision strategique : la monetisation ne porte pas sur l'IA elle-meme mais sur la plateforme (serveurs, sieges, collaboration).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifier le plan adapte** — Evaluez le nombre de sieges et les features necessaires\n2. **Creer l'abonnement** — Via la page Abonnements dans les Operations\n3. **Configurer les gates** — Chaque plan active automatiquement les feature gates correspondantes\n4. **Suivre le MRR** — Le dashboard affiche le MRR par plan et le detail des abonnements\n5. **Upgrader si besoin** — Le changement de plan est prorate automatiquement par Stripe",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "L'onglet **Abonnements** dans Operations affiche tous les abonnements actifs, leur plan, le MRR et les prochaines echeances. L'admin peut voir les details Stripe de chaque abonnement et gerer les changements de plan.",
      },
    ],
    linkedModule: {
      label: 'Abonnements',
      path: '/ops/subscriptions',
      icon: 'CreditCard',
    },
  },
  {
    slug: 'manage-subscriptions',
    title: 'Gerer les abonnements (creer, modifier, annuler)',
    duration: '5 min',
    glossaryTerms: ['subscription', 'proration', 'billing-cycle'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La gestion des abonnements Stripe couvre 3 operations principales :\n\n- **Creer** — Un nouvel abonnement est provisionne via Stripe Checkout ou l'API. Le client choisit son plan et sa devise.\n- **Modifier** — Changement de plan (upgrade/downgrade) avec proration automatique. Stripe calcule le montant au prorata de la periode restante.\n- **Annuler** — L'annulation peut etre immediate ou a la fin de la periode en cours. Une grace period de 7 jours est appliquee pour les impayes.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Une gestion fluide des abonnements reduit le churn involontaire et ameliore l'experience client. Les points cles :\n\n- Un upgrade sans friction augmente l'ARPU (Average Revenue Per User)\n- Une annulation douce (fin de periode) laisse une porte ouverte au retour\n- La proration automatique evite les litiges de facturation",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Creer un abonnement** — Le client passe par Stripe Checkout, selectionne son plan et sa devise\n2. **Modifier un plan** — Depuis l'admin, changer le plan via l'API PATCH /subscriptions/{id}\n3. **Annuler** — Choisir entre annulation immediate ou a la fin de la periode\n4. **Suivre les statuts** — active, past_due, canceled, trialing\n5. **Gerer les webhooks** — Stripe envoie des events pour chaque changement d'etat",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "L'admin peut gerer les abonnements depuis **Operations > Abonnements**. Le systeme recoit les webhooks Stripe (invoice.paid, customer.subscription.updated, etc.) et met a jour automatiquement les feature gates de l'organisation.",
      },
    ],
    linkedModule: {
      label: 'Abonnements',
      path: '/ops/subscriptions',
      icon: 'CreditCard',
    },
  },
  {
    slug: 'refunds-coupons',
    title: 'Remboursements et codes promo',
    duration: '4 min',
    glossaryTerms: ['refund', 'coupon', 'promotion-code'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Stripe Billing V2 supporte deux mecanismes commerciaux :\n\n**Remboursements** :\n- Remboursement total ou partiel d'une facture\n- Cree automatiquement un credit note dans Stripe\n- Le remboursement peut etre applique au wallet ou renvoye au moyen de paiement\n\n**Codes promo** :\n- Coupons Stripe avec reduction en pourcentage ou montant fixe\n- Duree : une fois, nombre de mois limite, ou permanent\n- Codes promo associes a un coupon, partageables par URL ou saisie manuelle",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les remboursements traites rapidement augmentent la confiance client et reduisent les disputes (chargebacks). Les codes promo sont un levier d'acquisition puissant :\n\n- Offrir 20% de reduction aux parrains reduit le CAC\n- Les coupons de retention ciblent les clients a risque de churn\n- Les promotions saisonnieres generent des pics d'inscription",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "**Remboursement** :\n1. Identifier la facture dans Operations > Abonnements\n2. Cliquer sur 'Rembourser' et choisir le montant (total ou partiel)\n3. Confirmer — le credit note Stripe est cree automatiquement\n\n**Code promo** :\n1. Creer un coupon dans Stripe Dashboard (ou via API)\n2. Generer un promotion code associe\n3. Distribuer le code aux clients cibles\n4. Le code est valide automatiquement lors du checkout",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Les remboursements sont accessibles depuis la fiche abonnement dans **Operations**. Les codes promo actifs sont visibles dans le tableau de bord marketing et appliques automatiquement via le Referral Network.",
      },
    ],
    linkedModule: {
      label: 'Abonnements',
      path: '/ops/subscriptions',
      icon: 'CreditCard',
    },
  },
  {
    slug: 'grace-period-unpaid',
    title: 'Grace period et impayes',
    duration: '4 min',
    glossaryTerms: ['grace-period', 'dunning', 'past-due'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Quand un paiement echoue (carte expiree, fonds insuffisants), Stripe entre dans un cycle de relance automatique (dunning) :\n\n1. **Premiere tentative** — Le jour de l'echeance\n2. **Relance 1** — 3 jours apres l'echec\n3. **Relance 2** — 5 jours apres l'echec\n4. **Relance 3** — 7 jours apres l'echec (fin de grace period)\n\nPendant la grace period de 7 jours, l'abonnement passe en statut `past_due` mais l'acces aux fonctionnalites est maintenu. Apres 7 jours sans paiement, l'abonnement est annule et les feature gates sont desactivees.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le churn involontaire (echecs de paiement) represente jusqu'a 20-40% du churn total d'un SaaS. Une grace period bien configuree permet de :\n\n- Recuperer 60-70% des paiements echoues via les relances automatiques\n- Maintenir une bonne experience utilisateur malgre un probleme technique\n- Reduire le churn involontaire de moitie\n\nSans grace period, un simple changement de carte bancaire pourrait causer la perte d'un client fidele.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Configurer le dunning** — Dans Stripe Dashboard > Settings > Subscriptions > Smart Retries\n2. **Definir la grace period** — 7 jours est le standard recommande\n3. **Personnaliser les emails** — Stripe envoie des emails de relance automatiques\n4. **Monitorer les past_due** — Le dashboard admin affiche les abonnements en souffrance\n5. **Action manuelle** — Contacter le client si la relance automatique echoue",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le systeme ecoute les webhooks `invoice.payment_failed` et `customer.subscription.updated` pour gerer automatiquement les transitions de statut. Les abonnements `past_due` sont signales dans le dashboard Operations avec un badge d'alerte.",
      },
    ],
    linkedModule: {
      label: 'Abonnements',
      path: '/ops/subscriptions',
      icon: 'CreditCard',
    },
  },
  {
    slug: 'multi-currency',
    title: 'Multi-devises (EUR / USD / XOF)',
    duration: '3 min',
    glossaryTerms: ['currency', 'price', 'locale'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Feedback Orchestrator supporte 3 devises pour servir ses marches cibles :\n\n- **EUR** — Marche europeen (France, Belgique, etc.)\n- **USD** — Marche international (US, UK, etc.)\n- **XOF** — Marche africain francophone (Senegal, Cote d'Ivoire, etc.)\n\nChaque plan Stripe a 3 Price objects (un par devise). Le prix est fixe manuellement par devise (pas de conversion automatique) pour optimiser le positionnement local. Par exemple, le plan Starter peut etre a 29 EUR, 29 USD et 15 000 XOF.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Proposer des prix en devise locale est essentiel pour :\n\n- **Reduire la friction** — Un client africain ne veut pas payer en EUR\n- **Optimiser le pricing** — Adapter les prix au pouvoir d'achat local\n- **Eviter les frais de change** — Le client paie dans sa devise, Stripe gere la conversion\n- **Augmenter la conversion** — Jusqu'a 30% d'amelioration du taux de conversion avec des prix locaux",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definir les prix par devise** — Dans la matrice de prix (docs/business/PLANS-FEATURES-MATRIX-2026.md)\n2. **Creer les Stripe Prices** — Un Price par plan par devise (15 Prices au total pour 5 plans x 3 devises)\n3. **Detecter la devise** — Via la localisation du client ou son choix explicite\n4. **Afficher le bon prix** — Le frontend affiche le prix dans la devise detectee\n5. **Facturer** — Stripe emet la facture dans la devise du Price selectionne",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La devise est detectee automatiquement via la localisation du navigateur et peut etre changee manuellement. La landing page et la page pricing affichent les prix dans la devise selectionnee. L'admin peut voir la repartition du MRR par devise dans Operations.",
      },
    ],
    linkedModule: {
      label: 'Abonnements',
      path: '/ops/subscriptions',
      icon: 'CreditCard',
    },
  },
];
